/**
 * Parse and serialize Personal Information / Contact section content for structured form editing.
 * Used so the left pane can show First name, Last name, Phone, Email, Address, Web, Profile instead of one textarea.
 */

export interface PersonalInfoFields {
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  zipCode: string;
  state: string;
  web: string;
  profile: string;
  showPicture?: boolean;
  customFields?: Array<{ key: string; value: string }>;
}

const EMPTY: PersonalInfoFields = {
  firstName: '',
  lastName: '',
  title: '',
  phone: '',
  email: '',
  street: '',
  city: '',
  zipCode: '',
  state: '',
  web: '',
  profile: '',
  showPicture: true,
  customFields: [],
};

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const PHONE_REGEX = /\+?[\d\s\-().]{10,}/;

/** Extract email from a line or full text */
function extractEmail(text: string): string {
  const m = text.match(EMAIL_REGEX);
  return m ? m[0] : '';
}

/** Extract phone from a line or full text */
function extractPhone(text: string): string {
  const m = text.match(PHONE_REGEX);
  return m ? m[0].trim() : '';
}

/** Check if line looks like a URL (LinkedIn, personal site, etc.) */
function isWebLine(line: string): boolean {
  const t = line.trim();
  return /^https?:\/\//i.test(t) || /linkedin\.com|github\.com|\.com\s*$/i.test(t);
}

/**
 * Parse raw "Personal Information" / "Contact" section content into structured fields.
 */
export function parsePersonalInfoContent(content: string): PersonalInfoFields {
  if (!content || !content.trim()) return { ...EMPTY };
  const lines = content.split(/\n/).map((l) => l.trim()).filter(Boolean);
  const out = { ...EMPTY };
  const rest: string[] = [];

  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.startsWith('email') || lower.startsWith('e-mail')) {
      out.email = line.replace(/^(email|e-mail)\s*:?\s*/i, '').trim() || extractEmail(line);
      continue;
    }
    if (lower.startsWith('phone') || lower.startsWith('tel') || lower.startsWith('mobile')) {
      out.phone = line.replace(/^(phone|tel|mobile)\s*:?\s*/i, '').trim() || extractPhone(line);
      continue;
    }
    if (lower.startsWith('web') || lower.startsWith('linkedin') || lower.startsWith('url')) {
      out.web = line.replace(/^(web|linkedin|url)\s*:?\s*/i, '').trim();
      continue;
    }
    if (lower.startsWith('address')) {
      const addr = line.replace(/^address\s*:?\s*/i, '').trim();
      if (addr) {
        const parts = addr.split(/,\s*/).map((p) => p.trim());
        if (parts.length >= 4) {
          out.street = parts[0];
          out.city = parts[1];
          out.zipCode = parts[2];
          out.state = parts[3];
        } else if (parts.length === 3) {
          out.city = parts[0];
          out.zipCode = parts[1];
          out.state = parts[2];
        } else if (parts.length >= 1) {
          const withZip = addr.match(/(.+?)\s+(\d{4,6})\s*,?\s*([A-Za-z]{2})?$/);
          if (withZip) {
            out.city = withZip[1].replace(/,/g, '').trim();
            out.zipCode = withZip[2];
            if (withZip[3]) out.state = withZip[3];
          } else {
            out.city = parts[0];
          }
        }
      }
      continue;
    }
    if (EMAIL_REGEX.test(line) && !out.email) {
      out.email = extractEmail(line);
      continue;
    }
    if (PHONE_REGEX.test(line) && line.length < 35 && !out.phone) {
      out.phone = extractPhone(line);
      continue;
    }
    if (isWebLine(line) && !out.web) {
      out.web = line;
      continue;
    }
    rest.push(line);
  }

  if (rest.length > 0) {
    const first = rest[0];
    const parts = first.split(/\s+\|\s+|\s+–\s+|\s+-\s+/).map((p) => p.trim()).filter(Boolean);
    const firstPart = parts[0] || '';
    if (firstPart.length > 0 && firstPart.length < 120) {
      const looksLikeTitle = /\b(manager|engineer|director|lead|analyst|developer|technical|product|sr\.?|sr|pm|consultant)\b/i.test(firstPart);
      const namePart = parts.length > 1 ? parts.slice(1).join(' ').trim() : '';
      const nameTokens = namePart ? namePart.split(/\s+/) : firstPart.split(/\s+/);
      if (parts.length > 1 && namePart) {
        out.title = firstPart;
        if (nameTokens.length >= 3) {
          out.lastName = nameTokens.pop()!;
          out.firstName = nameTokens.pop()!;
          const roleMid = nameTokens.join(' ');
          if (roleMid) out.title = out.title + (out.title ? ' | ' : '') + roleMid;
        } else if (nameTokens.length >= 2) {
          out.lastName = nameTokens.pop()!;
          out.firstName = nameTokens.join(' ');
        } else {
          out.firstName = namePart;
        }
      } else if (!looksLikeTitle && nameTokens.length >= 2) {
        out.lastName = nameTokens.pop()!;
        out.firstName = nameTokens.join(' ');
      } else if (!looksLikeTitle) {
        out.firstName = firstPart;
      } else {
        out.title = firstPart;
      }
    }
    const addrLine = rest.find((l) => /\d{4,6}/.test(l) && (/,?\s*[A-Za-z]{2}\s*$/.test(l) || l.length < 50));
    if (addrLine && !out.city && !out.zipCode) {
      const match = addrLine.match(/^(.+?)\s+(\d{4,6})\s*,?\s*([A-Za-z]{2})?$/);
      if (match) {
        out.city = match[1].replace(/,/g, '').trim();
        out.zipCode = match[2];
        if (match[3]) out.state = match[3];
      } else {
        out.city = addrLine;
      }
    }
    const profileLines = rest.filter((l) => l !== rest[0] && l !== addrLine && (l.length > 35 || !PHONE_REGEX.test(l)));
    if (profileLines.length > 0) out.profile = profileLines.join('\n');
  }

  return out;
}

/**
 * Serialize structured fields back to a single content string for storage and preview.
 */
export function serializePersonalInfoContent(f: PersonalInfoFields): string {
  const lines: string[] = [];
  const fullName = [f.firstName, f.lastName].filter(Boolean).join(' ');
  if (f.title && fullName) {
    lines.push(`${f.title} ${fullName}`);
  } else if (fullName) {
    lines.push(fullName);
  } else if (f.title) {
    lines.push(f.title);
  }
  if (f.phone) lines.push(`Phone: ${f.phone}`);
  if (f.email) lines.push(`Email: ${f.email}`);
  const addrParts = [f.street, f.city, f.zipCode, f.state].filter(Boolean);
  if (addrParts.length > 0) lines.push(`Address: ${addrParts.join(', ')}`);
  if (f.web) lines.push(f.web.startsWith('http') ? f.web : `Web: ${f.web}`);
  const custom = f.customFields ?? [];
  custom.forEach(({ key, value }) => { if (key.trim()) lines.push(`${key.trim()}: ${value.trim()}`); });
  if (f.profile.trim()) lines.push('', f.profile.trim());
  return lines.join('\n');
}
